import type { Industry } from '../../types/account.js';
import {
  REPORT_TEMPLATE_LABELS,
  isReportTemplate,
  type ReportTemplate,
  type ReportTemplateSource,
} from '../../types/reportTemplate.js';

// ⚠️⚠️ WHICH CLIENT REPORT TEMPLATE AN ACCOUNT GETS — the ONE resolver
// (2026-11-11, brief rounds 3 and 4). PURE: no I/O, no clock, no env. The caller
// passes the roster slice and the one flag.
//
// ORDER (round 4 §1), nearest/most-deliberate first:
//   1. the account's OWN `reportTemplate`
//   2. the PARENT CHAIN, nearest set value wins (dealership → group)
//   3. the MARQUE — the account's own `brandAccountId` pointer
//   4. the legacy brand-level `portalMetaGoalReport` tick → goal_split
//   5. the INDUSTRY default, ONLY with AUTOMOTIVE_TEMPLATE_DEFAULT on
//   6. standard
//
// ⚠️ A GROUP'S CHOICE BEATS THE MARQUE'S, deliberately: a dealer group can opt
// its dealerships out of a marque-wide setting. Groups and brands are separate
// roots — the marque is a POINTER, not an ancestor — so step 3 is the account's
// own pointer, never a walk.
//
// ⚠️ ABSENT / NULL MEANS INHERIT, not `standard`. Only a deliberately chosen
// value stops the walk. An unrecognised stored value (a hand edit, a future
// template read by an older build) is treated as ABSENT rather than trusted.
//
// ⚠️ ALL-OR-NOTHING ACROSS A SCOPE (`resolveScopeTemplate`): a client sees ONE
// report over every account they hold, so a mixed scope falls to `standard`, and
// an EMPTY scope is `standard` explicitly (`[].every()` is true — the same trap
// metaGoalReportEnabled documents).

/** The slice of an Account the resolver reads. */
export interface TemplateAccount {
  id: string;
  parentAccountId?: string | null;
  /** The marque pointer. */
  brandAccountId?: string | null;
  reportTemplate?: ReportTemplate | string | null;
  industry?: Industry | string | null;
}

export interface TemplateResolution {
  template: ReportTemplate;
  source: ReportTemplateSource;
  /** The account whose value won, for the "inherited from" label. Null for legacy / industry / default. */
  sourceAccountId: string | null;
}

export interface TemplateContext {
  /** Every account that might be walked to (the roster). */
  accounts: ReadonlyMap<string, TemplateAccount>;
  /**
   * Account id → its brand-ancestor id, from `computeBrandAncestorMap` — the ONE
   * brand resolver. Used only for the legacy tick (step 4), exactly as today.
   */
  brandByAccount: ReadonlyMap<string, string | null>;
  /** Whether a brand account carries the legacy `portalMetaGoalReport` tick. */
  brandHasLegacyGoalSplit: (brandAccountId: string) => boolean;
  /** AUTOMOTIVE_TEMPLATE_DEFAULT. Off at deploy. */
  automotiveDefaultOn: boolean;
}

/** Bound on the parent walk. Real trees are three deep; this stops a cycle in bad data. */
const MAX_PARENT_HOPS = 12;

function setValue(a: TemplateAccount | undefined): ReportTemplate | null {
  const v = a?.reportTemplate;
  return isReportTemplate(v) ? v : null;
}

export function resolveReportTemplateForAccount(
  account: TemplateAccount,
  ctx: TemplateContext,
): TemplateResolution {
  // 1. own
  const own = setValue(account);
  if (own) return { template: own, source: 'own', sourceAccountId: account.id };

  // 2. parent chain — nearest set value wins. A visited-set as well as the hop
  //    bound: either alone would do, both cost nothing.
  const seen = new Set<string>([account.id]);
  let cursor = account.parentAccountId ?? null;
  for (let hops = 0; cursor && hops < MAX_PARENT_HOPS && !seen.has(cursor); hops++) {
    seen.add(cursor);
    const parent = ctx.accounts.get(cursor);
    if (!parent) break;
    const v = setValue(parent);
    if (v) return { template: v, source: 'parent', sourceAccountId: parent.id };
    cursor = parent.parentAccountId ?? null;
  }

  // 3. marque
  const marqueId = account.brandAccountId ?? null;
  if (marqueId) {
    const v = setValue(ctx.accounts.get(marqueId));
    if (v) return { template: v, source: 'marque', sourceAccountId: marqueId };
  }

  // 4. legacy brand tick (through the brand resolver, as today)
  const brand = ctx.brandByAccount.get(account.id) ?? null;
  if (brand !== null && ctx.brandHasLegacyGoalSplit(brand)) {
    return { template: 'goal_split', source: 'legacy', sourceAccountId: null };
  }

  // 5. industry default — only behind the flag. A null industry never matches.
  if (ctx.automotiveDefaultOn && account.industry === 'automotive_franchise') {
    return { template: 'automotive', source: 'industry', sourceAccountId: null };
  }

  // 6.
  return { template: 'standard', source: 'default', sourceAccountId: null };
}

/**
 * The template for a whole scope. Every account resolving to the same template →
 * that template; anything mixed, or nothing in scope, → `standard`.
 *
 * ⚠️ An id in scope that is NOT on the roster is treated as unresolvable and
 * makes the scope `standard` — guessing a template for an account we cannot see
 * would put a client on a layout nobody chose.
 */
export function resolveScopeTemplate(
  accountIds: readonly string[],
  ctx: TemplateContext,
): ReportTemplate {
  if (accountIds.length === 0) return 'standard';
  let agreed: ReportTemplate | null = null;
  for (const id of accountIds) {
    const account = ctx.accounts.get(id);
    if (!account) return 'standard';
    const t = resolveReportTemplateForAccount(account, ctx).template;
    if (agreed === null) agreed = t;
    else if (agreed !== t) return 'standard';
  }
  return agreed ?? 'standard';
}

/**
 * The sentence the account page shows beside the select: what this account gets
 * if nothing is chosen on it, and which level that came from. ⚠️ Names the LEVEL
 * that won (own / group / marque / default) per round 4 §1.
 */
export function inheritedTemplateLabel(
  resolution: TemplateResolution,
  nameOf: (accountId: string) => string | null,
): string {
  const label = REPORT_TEMPLATE_LABELS[resolution.template];
  const from = resolution.sourceAccountId ? nameOf(resolution.sourceAccountId) : null;
  switch (resolution.source) {
    case 'own':
      return `${label} (set on this account)`;
    case 'parent':
      return from ? `${label} (from ${from})` : `${label} (from a parent account)`;
    case 'marque':
      return from ? `${label} (from the ${from} marque)` : `${label} (from the marque)`;
    case 'legacy':
      return `${label} (from the brand's older goal-split tick)`;
    case 'industry':
      return `${label} (automotive default)`;
    case 'default':
      return `${label} (default)`;
  }
}
